import type ICommonDataStructure from "src/components/streaming/streamingtitlewithproviders/CommonDataStructure";
import { isFilmwebIdGraphQLUserContent, type IFilmwebIdGraphQLUserContent, type IFilmwebIdMovieIdentifierType } from "src/lib/filmwebid/useMovieIdentifier";
import type { FilmwebIdMovieIdentifier, Movie, StreamingContent, WatchableContent } from "src/lib/movieinfo/movieinfotypes";
import { isMovie, isStreamingContent, isWatchableContent } from "src/lib/movieinfo/typeGuards";
import { isFilmomtale, type IFilmomtale } from "src/lib/types/Filmomtale";
import { isFilmwebSearchDocument, isKinoDkSearchDocument, type ISearchDocument } from "src/lib/types/search";

const BRAND = process.env.NEXT_PUBLIC_WEBSITE_BRAND!;

/**
 *
 * @param content The content from movieInfoQS
 * @param isCinemaRelevant
 * @param movieIdentifiers
 * @returns
 */
export function buildFilmUrl(content: IFilmwebIdGraphQLUserContent | IFilmomtale | Movie | ICommonDataStructure | StreamingContent | WatchableContent | ISearchDocument, movieIdentifiers?: FilmwebIdMovieIdentifier[] | null, overrideCinemaRelevant?: boolean) {

    let filmwebMovieId: string | null = null;
    let streamingId: number | null;
    let isSeries: boolean;
    let isCinemaRelevant: boolean;
    if (isFilmwebIdGraphQLUserContent(content)) {
        filmwebMovieId = content.watchableContent?.movieId || null;
        streamingId = content.watchableContent?.streamingContentId || null;
        isSeries = content.watchableContent?.isSeries ?? false;
        isCinemaRelevant = content.watchableContent?.isCinemaRelevant ?? false;
        movieIdentifiers ??= content.knownMovieIdentifiers as FilmwebIdMovieIdentifier[] | null | undefined;
    }
    else if (isMovie(content)) {
        filmwebMovieId = BRAND === "filmweb" ? content.mainVersionId : content.nffMovieId;
        streamingId = null;
        isSeries = false;
        isCinemaRelevant = true;
    }
    else if (isFilmomtale(content)) {
        filmwebMovieId = BRAND === "filmweb" ? content.mainVersionEDI || null : `${content.fomId ?? content.drupalSlug?.current}` || null;
        streamingId = null;
        isSeries = false;
        isCinemaRelevant = true;
    }
    else if (isStreamingContent(content)) {
        filmwebMovieId = content.filmwebMovieId || null;
        streamingId = content.id || null;
        isSeries = content.isSeries;
        isCinemaRelevant = false;
    }
    else if (isWatchableContent(content)) {
        filmwebMovieId = content.movieId || null;
        streamingId = content.streamingContentId || null;
        isSeries = content.isSeries;
        isCinemaRelevant = content.isCinemaRelevant;
    }
    else if (isFilmwebSearchDocument(content)) {
        filmwebMovieId = content.edi || null;
        streamingId = content.streamingId || null;
        isSeries = !content.isMovie;
        isCinemaRelevant = !content.isStreaming;
    }
    else if (isKinoDkSearchDocument(content)) {
        filmwebMovieId = content.foMId ? String(content.foMId) : null;
        streamingId = null;
        isSeries = false;
        isCinemaRelevant = true;
    }
    else if ("filmwebMovieId" in content) {
        // ICommonDataStructure
        filmwebMovieId = content.filmwebMovieId ?? null;
        streamingId = content.streamingId || null;
        isSeries = content.isSeries;
        isCinemaRelevant = content.isCinemaRelevant;
    }
    else {
        filmwebMovieId = null;
        streamingId = null;
        isSeries = false;
        isCinemaRelevant = true;
    }

    if (typeof overrideCinemaRelevant === "boolean") {
        isCinemaRelevant = overrideCinemaRelevant;
    }

    if (filmwebMovieId || streamingId) {
        if (filmwebMovieId && (isCinemaRelevant || !streamingId)) {
            return `/film/${filmwebMovieId}`
        }
        else if (streamingId) {
            // always true here, but TypeScript can't work that out from the conditions above
            return getStreamingGuideUrl(streamingId, isSeries);
        }
    }
    else if (isFilmwebIdGraphQLUserContent(content) && content.movieIdentifier) {
        switch (content.movieIdentifier.type as IFilmwebIdMovieIdentifierType) {
            case "EDI":
                if (BRAND === "filmweb") {
                    // Only use EDI for filmweb
                    return `/film/${content.movieIdentifier.value}`;
                }
            case "FoM":
                if (BRAND === "kinodk") {
                    // Only use FoM identifiers for kinodk
                    return `/film/${content.movieIdentifier.value}`;
                }
            case "StreamingId":
                // streamingId is always null here (we only get here without one), so use the identifier's value
                if (BRAND === "filmweb" && content.movieIdentifier.value) {
                    return getStreamingGuideUrl(content.movieIdentifier.value, isSeries);
                }
            case "Slug":
                return `/film/${content.movieIdentifier.value}`
            case "KinoDk_Drupal":
                return `/node/${content.movieIdentifier.value}`
        }
    }

    // IF we get here, as a final fallback find some values from the movieIDentifiers.
    if (movieIdentifiers?.length) {
        const slugIdentifier = movieIdentifiers.find(id => id.type == "Slug");
        if (slugIdentifier?.value) {
            return `/film/${slugIdentifier.value}`
        }
        const drupalIdentifier = movieIdentifiers.find(id => id.type == "KinoDk_Drupal");
        if (drupalIdentifier?.value) {
            return `/node/${drupalIdentifier.value}`
        }
    }
    else {
        //console.log(content);
        // Should never happen
        return `#`
    }
}

export function getStreamingGuideUrl(streamingId: number | string, isSeries?: boolean): string {
	return `/streamingguide/${isSeries ? "serie" : "film"}/${streamingId}`;
}